import { useDispatch } from 'react-redux'
import { Aurora, Product, Project } from '../../types'
import { useState } from 'react'
import { getAurora } from '../../services/aurora'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography
} from '@mui/material'
import AuroraBlockDisplay from '../AuroraBlockDisplay'
import AuroraDetailsList from '../AuroraDetailsList'
import { NavLink } from 'react-router-dom'
import { OpenInNew, QrCode } from '@mui/icons-material'
import LoadingState from '../LoadingState'
import QrReaderButton from '../QrReaderButton'
import CodeInput from '../CodeInput'
import { createErrorNotification } from '../../redux/slices/notifications'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'

export interface AuroraSetupStepProps {
  project?: Project
  aurora: Aurora | null
  product?: Product
  loading?: boolean
  onAuroraChange: (aurora: Aurora | null) => void
  onNext: () => void
  onBack: () => void
}

export default function AuroraSetupStep({
  project,
  aurora,
  product,
  loading = false,
  onAuroraChange,
  onNext,
  onBack
}: AuroraSetupStepProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()

  const [isLoading, setLoading] = useState<boolean>(false)

  const handleCodeChange = async (code: string) => {
    if (code.length !== 6) {
      return
    }

    try {
      setLoading(true)

      // Try to fetch aurora with or without the prefix.
      const prefixes = ['', 'VILP_', 'VILP_id']
      for (const prefix of prefixes) {
        try {
          const aurora = await getAurora(`${prefix}${code}`)
          onAuroraChange(aurora)
          return
        } catch {
          continue
        }
      }

      throw new Error()
    } catch {
      dispatch(createErrorNotification(t('auroraNotFound')))
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    onAuroraChange(null)
    onBack()
  }

  return (
    <Card>
      {/*<CardHeader title={t('auroraSetup')} />*/}
      <CardContent sx={{ pt: 10, px: 8 }}>
        {aurora ? (
          <>
            <AuroraBlockDisplay aurora={aurora} sx={{ mb: 2 }} />

            <AuroraDetailsList aurora={aurora} withCompany={false} withTimestamps={false} />

            {project && aurora?.asset?.groupId && (
              <Alert
                severity="warning"
                sx={{ mt: 4 }}
                action={
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    component={NavLink}
                    endIcon={<OpenInNew fontSize="small" />}
                    to={`/auroras/group/${aurora.asset.groupId}`}
                  >
                    {t('showAssetGroup')}
                  </Button>
                }
              >
                {t('auroraBelongsToAssetGroup')}
              </Alert>
            )}

            {product && aurora?.productId && (
              <Alert severity="warning">
                <AlertTitle sx={{ m: 0 }}>
                  {t('auroraInstalledToProductWarning', {
                    product: aurora.productId
                  })}
                </AlertTitle>
              </Alert>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: (theme) => theme.palette.background.default,
              borderRadius: 2,
              p: 8
            }}
          >
            {isLoading ? (
              <LoadingState align="center" />
            ) : (
              <>
                <QrCode sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 4 }}>
                  {t('readQrCodeOrEnterCode')}
                </Typography>
              </>
            )}
            <QrReaderButton onChange={handleCodeChange} />
            <CodeInput disabled={isLoading} onChange={handleCodeChange} />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="primary" onClick={handleBack}>
          {t('back')}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={loading}
          disabled={Boolean(
            aurora === null || (project && aurora?.asset?.groupId) || (product && aurora?.productId)
          )}
          onClick={onNext}
        >
          {t('next')}
        </LoadingButton>
      </CardActions>
    </Card>
  )
}
